import React, { useEffect, useState, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { Event } from '../types/supabase';
import { formatDateDisplay, getTimezoneAbbreviation } from '../utils/calendar';
import { describeRecurrence, getNextOccurrence } from '../lib/recurrence';
import toast from 'react-hot-toast';
import {
  Trash,
  Mail,
  LogOut,
  ThumbsUp,
  ThumbsDown,
  Heart,
  MessageCircle,
  Calendar,
  Users,
  Home,
  Phone,
  MapPin,
  User,
  ChevronUp,
  UserCheck,
  Menu,
  X,
  Plus,
  CheckCircle,
  RotateCcw,
  BookOpen,
  ShieldAlert,
  Repeat,
  Pencil
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ChangePasswordForm from './ChangePasswordForm';
import DeleteAccountSection from './DeleteAccountSection';
import AdminAddEventModal from './AdminAddEventModal';
import AdminMembers from './AdminMembers';
import AdminReading from './AdminReading';
import EventSignupList, { EventSignup } from './EventSignupList';

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string | null;
  message: string;
  created_at: string;
  read: boolean;
  phone: string | null;
  zip_code: string | null;
  responded: boolean;
  responded_by: string | null;
  responded_at: string | null;
  referral_source: string;
}

interface Request {
  id: string;
  name: string | null;
  type: 'prayer' | 'praise';
  description: string;
  created_at: string;
}

type TabId = 'dashboard' | 'events' | 'messages' | 'requests' | 'members' | 'reading' | 'account';

const TAB_ROLE_MAP: Record<string, string[]> = {
  dashboard: ['admin'],
  events: ['admin', 'events'],
  messages: ['admin'],
  requests: ['admin'],
  members: ['admin'],
  reading: ['admin', 'reading'],
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [volunteers, setVolunteers] = useState<EventSignup[]>([]);
  const [attendees, setAttendees] = useState<EventSignup[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('account');
  const [userEmail, setUserEmail] = useState<string>('');
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [eventsTab, setEventsTab] = useState<'requests' | 'upcoming' | 'past' | 'deleted'>('requests');
  const [messagesTab, setMessagesTab] = useState<'new' | 'completed'>('new');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const isAdmin = userRoles.includes('admin');

  const canAccess = (tab: string): boolean => {
    if (tab === 'account') return true;
    const allowed = TAB_ROLE_MAP[tab];
    if (!allowed) return false;
    return userRoles.some(r => allowed.includes(r));
  };

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) setUserEmail(user.email);
        if (user?.id) {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id);
          const roleList = (roles || []).map(r => r.role);
          setUserRoles(roleList);

          const hasAdmin = roleList.includes('admin');
          const hasEvents = roleList.includes('events');
          const hasReading = roleList.includes('reading');

          if (hasAdmin) {
            setActiveTab('dashboard');
          } else if (hasEvents) {
            setActiveTab('events');
          } else if (hasReading) {
            setActiveTab('reading');
          } else {
            setActiveTab('account');
          }

          if (hasAdmin || hasEvents) {
            fetchEvents();
            fetchVolunteers();
            fetchAttendees();
          }
          if (hasAdmin) {
            fetchContactMessages();
            fetchRequests();
          }
        }
      } catch (error) {
        console.error('Error initializing:', error);
      } finally {
        setRolesLoaded(true);
        setLoading(false);
      }
    };

    init();

    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  };

  const fetchVolunteers = async () => {
    try {
      const { data, error } = await supabase
        .from('event_volunteers')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setVolunteers(data || []);
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    }
  };

  const fetchAttendees = async () => {
    try {
      const { data, error } = await supabase
        .from('event_attendees')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAttendees(data || []);
    } catch (error) {
      console.error('Error fetching attendees:', error);
    }
  };

  const fetchContactMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setContactMessages(data || []);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    }
  };

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const updateEventSignupOptions = async (
    eventId: string,
    changes: Partial<Pick<Event, 'allow_volunteers' | 'allow_attendees'>>
  ) => {
    const previous = events;
    setEvents(prev => prev.map(e => (e.id === eventId ? { ...e, ...changes } : e)));
    const { error } = await supabase.from('events').update(changes).eq('id', eventId);
    if (error) {
      console.error('Error updating sign-up options:', error);
      setEvents(previous);
      toast.error('Failed to update sign-up options');
      return;
    }
    toast.success('Sign-up options updated');
  };

  const updateEventStatus = async (eventId: string, status: 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('events')
        .update({ status })
        .eq('id', eventId);
      if (error) throw error;
      toast.success(`Event ${status} successfully`);
      fetchEvents();
    } catch (error) {
      console.error('Error updating event status:', error);
      toast.error('Failed to update event status');
    }
  };

  const deleteEvent = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);
      if (error) throw error;
      toast.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const createEvent = async (eventData: {
    title: string;
    date: string;
    time: string;
    location: string;
    description: string;
    category: string;
    is_recurring: boolean;
    recurrence_frequency: Event['recurrence_frequency'];
    recurrence_end_date: string | null;
    timezone: string;
    allow_volunteers: boolean;
    allow_attendees: boolean;
  }) => {
    try {
      const { error } = await supabase
        .from('events')
        .insert([{
          ...eventData,
          status: 'approved',
          user_id: null,
          requester_name: null,
          requester_email: null
        }]);
      if (error) throw error;
      toast.success('Event created successfully');
      setShowAddEventModal(false);
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  const updateEvent = async (eventId: string, eventData: Parameters<typeof createEvent>[0]) => {
    try {
      const { error } = await supabase
        .from('events')
        .update(eventData)
        .eq('id', eventId);
      if (error) throw error;
      toast.success('Event updated');
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    }
  };

  const deleteRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('requests')
        .delete()
        .eq('id', requestId);
      if (error) throw error;
      toast.success('Request deleted successfully');
      fetchRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request');
    }
  };

  const markAsResponded = async (messageId: string, responded: boolean) => {
    try {
      const { error } = await supabase
        .from('contact_messages')
        .update({
          responded,
          responded_at: responded ? new Date().toISOString() : null,
          responded_by: responded ? userEmail : null,
        })
        .eq('id', messageId);
      if (error) throw error;
      toast.success(responded ? 'Marked as completed' : 'Moved back to new');
      fetchContactMessages();
    } catch (error) {
      console.error('Error updating message:', error);
      toast.error('Failed to update message');
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  const allSidebarItems = useMemo(() => [
    { id: 'dashboard' as TabId, label: 'Dashboard', icon: Home, badge: 0 },
    { id: 'events' as TabId, label: 'Events', icon: Calendar, badge: events.filter(e => e.status === 'pending').length },
    { id: 'messages' as TabId, label: 'Join Requests', icon: Mail, badge: contactMessages.filter(msg => !msg.responded).length },
    { id: 'members' as TabId, label: 'Members', icon: Users, badge: 0 },
    { id: 'reading' as TabId, label: 'Reading', icon: BookOpen, badge: 0 },
  ], [events, contactMessages]);

  const sidebarItems = useMemo(
    () => allSidebarItems.filter(item => canAccess(item.id)),
    [allSidebarItems, userRoles]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderNoAccess = () => (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-6">
        <ShieldAlert className="w-8 h-8 text-gray-400" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">No sections available</h2>
      <p className="text-gray-500 text-center max-w-md">
        You don't have any roles assigned yet. Contact an admin to get access to specific sections of the admin panel.
      </p>
    </div>
  );

  const renderDashboard = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center space-x-3">
          <Home className="w-6 sm:w-7 h-6 sm:h-7 text-gray-900" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        </div>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Welcome to the Monday Night Group admin panel</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Recent Events</h3>
          </div>
          <div className="p-4 sm:p-6">
            {events.slice(0, 5).map((event) => (
              <div key={event.id} className="flex items-center justify-between gap-3 py-3 border-b border-gray-100 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate text-sm sm:text-base">{event.title}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{formatDateDisplay(event.date)}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                  event.status === 'approved' ? 'bg-green-100 text-green-800' :
                  event.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {event.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-medium text-gray-900">Recent Messages</h3>
          </div>
          <div className="p-4 sm:p-6">
            {contactMessages.slice(0, 5).map((message) => (
              <div key={message.id} className="flex items-start justify-between gap-3 py-3 border-b border-gray-100 last:border-b-0">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{message.name}</p>
                  <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">{message.message}</p>
                  <p className="text-xs text-gray-400">{new Date(message.created_at).toLocaleDateString()}</p>
                </div>
                {!message.read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderEventCard = (event: Event) => {
    const eventVolunteers = volunteers.filter(v => v.event_id === event.id);
    const repeats = describeRecurrence(event);
    const nextDate = repeats ? getNextOccurrence(event) : null;
    return (
      <div key={event.id} className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{event.title}</h3>
              <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                event.status === 'approved' ? 'bg-green-100 text-green-800' :
                event.status === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {event.status}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 text-sm text-gray-600 mb-4">
              <div className="truncate"><strong>{event.is_recurring ? 'Starts:' : 'Date:'}</strong> {formatDateDisplay(event.date)}</div>
              <div className="truncate"><strong>Time:</strong> {event.time} {getTimezoneAbbreviation(event.timezone)}</div>
              <div className="truncate"><strong>Category:</strong> {event.category}</div>
            </div>
            <div className="mb-4 text-sm sm:text-base">
              {repeats && (
                <div className="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-xs sm:text-sm font-medium">
                  <Repeat className="w-3.5 h-3.5" />
                  {repeats}
                  {event.status === 'approved' && (
                    <span className="text-blue-600 font-normal">
                      {nextDate ? ` · next ${formatDateDisplay(nextDate)}` : ' · ended'}
                    </span>
                  )}
                </div>
              )}
              <div className="break-words"><strong>Location:</strong> {event.location}</div>
              <div className="mt-2 break-words"><strong>Description:</strong> {event.description}</div>
            </div>
            {(event.requester_name || event.requester_email) && (
              <div className="text-sm text-gray-600 mb-4 break-words">
                <strong>Requested by:</strong> {event.requester_name} ({event.requester_email})
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-600 font-medium">Sign-ups:</span>
              <button
                onClick={() => updateEventSignupOptions(event.id, { allow_attendees: !event.allow_attendees })}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                  event.allow_attendees
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
                title={event.allow_attendees ? 'Turn off the Attend button' : 'Turn on the Attend button'}
              >
                <UserCheck className="w-3.5 h-3.5" />
                Attend {event.allow_attendees ? 'on' : 'off'}
              </button>
              <button
                onClick={() => updateEventSignupOptions(event.id, { allow_volunteers: !event.allow_volunteers })}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                  event.allow_volunteers
                    ? 'bg-red-50 border-red-300 text-red-800 hover:bg-red-100'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
                title={event.allow_volunteers ? 'Turn off the Volunteer button' : 'Turn on the Volunteer button'}
              >
                <Heart className="w-3.5 h-3.5" />
                Volunteer {event.allow_volunteers ? 'on' : 'off'}
              </button>
            </div>
            <EventSignupList
              title="Attendees"
              icon={UserCheck}
              iconClass="text-emerald-600"
              people={attendees.filter(a => a.event_id === event.id)}
            />
            <EventSignupList
              title="Volunteers"
              icon={Heart}
              iconClass="text-red-600"
              people={eventVolunteers}
            />
          </div>
          <div className="flex sm:flex-col gap-2 sm:ml-4">
            <button
              onClick={() => setEditingEvent(event)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg sm:rounded-full transition-colors flex-1 sm:flex-none"
              title="Edit event"
            >
              <Pencil className="w-5 h-5 mx-auto" />
            </button>
            {event.status === 'pending' && (
              <>
                <button
                  onClick={() => updateEventStatus(event.id, 'rejected')}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg sm:rounded-full transition-colors flex-1 sm:flex-none"
                  title="Reject event"
                >
                  <ThumbsDown className="w-5 h-5 mx-auto" />
                </button>
                <button
                  onClick={() => updateEventStatus(event.id, 'approved')}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg sm:rounded-full transition-colors flex-1 sm:flex-none"
                  title="Approve event"
                >
                  <ThumbsUp className="w-5 h-5 mx-auto" />
                </button>
              </>
            )}
            {event.status === 'approved' && (
              <button
                onClick={() => updateEventStatus(event.id, 'rejected')}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg sm:rounded-full transition-colors flex-1 sm:flex-none"
                title="Reject event"
              >
                <ThumbsDown className="w-5 h-5 mx-auto" />
              </button>
            )}
            {event.status === 'rejected' && (
              <>
                <button
                  onClick={() => updateEventStatus(event.id, 'approved')}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg sm:rounded-full transition-colors flex-1 sm:flex-none"
                  title="Restore event"
                >
                  <ThumbsUp className="w-5 h-5 mx-auto" />
                </button>
                <button
                  onClick={() => deleteEvent(event.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg sm:rounded-full transition-colors flex-1 sm:flex-none"
                  title="Permanently delete"
                >
                  <Trash className="w-5 h-5 mx-auto" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const pendingEvents = events.filter(e => e.status === 'pending');
    const isUpcoming = (e: Event) =>
      e.is_recurring ? getNextOccurrence(e) !== null : new Date(e.date) >= today;
    const upcomingEvents = events.filter(e => e.status === 'approved' && isUpcoming(e));
    const pastEvents = events.filter(e => e.status === 'approved' && !isUpcoming(e));
    const rejectedEvents = events.filter(e => e.status === 'rejected');

    const displayedEvents =
      eventsTab === 'requests' ? pendingEvents :
      eventsTab === 'upcoming' ? upcomingEvents :
      eventsTab === 'past' ? pastEvents :
      rejectedEvents;

    const tabs: { id: typeof eventsTab; label: string; count: number; badge?: boolean }[] = [
      { id: 'requests', label: 'Requests', count: pendingEvents.length, badge: pendingEvents.length > 0 },
      { id: 'upcoming', label: 'Upcoming', count: upcomingEvents.length },
      { id: 'past', label: 'Past', count: pastEvents.length },
      { id: 'deleted', label: 'Rejected', count: rejectedEvents.length },
    ];

    const emptyLabels: Record<typeof eventsTab, string> = {
      requests: 'No pending event requests.',
      upcoming: 'No upcoming approved events.',
      past: 'No past approved events.',
      deleted: 'No rejected events.',
    };

    return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <Calendar className="w-6 sm:w-7 h-6 sm:h-7 text-gray-900" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Events Management</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage event submissions and approvals</p>
        </div>
        <button
          onClick={() => setShowAddEventModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2 whitespace-nowrap"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Event</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      <div className="flex border-b border-gray-200 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setEventsTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              eventsTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              eventsTab === tab.id
                ? tab.id === 'deleted' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                : tab.badge ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {displayedEvents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{emptyLabels[eventsTab]}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-200">
            {displayedEvents.map((event) => renderEventCard(event))}
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderMessageCard = (message: ContactMessage) => (
    <div key={message.id} className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
        <div className="flex items-start justify-between sm:items-center gap-3 flex-1 min-w-0">
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{message.name}</h3>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mt-1">
              <div className="flex items-center min-w-0">
                <Mail className="w-4 h-4 mr-1 flex-shrink-0" />
                <a href={`mailto:${message.email}`} className="text-blue-600 hover:text-blue-800 truncate">
                  {message.email}
                </a>
              </div>
              {message.phone && (
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-1 flex-shrink-0" />
                  <a href={`tel:${message.phone}`} className="text-blue-600 hover:text-blue-800">
                    {message.phone}
                  </a>
                </div>
              )}
              {message.zip_code && (
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                  <span>{message.zip_code}</span>
                </div>
              )}
            </div>
          </div>
          {!message.read && !message.responded && (
            <div className="w-3 h-3 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-xs sm:text-sm text-gray-500">
            {new Date(message.created_at).toLocaleString()}
          </div>
          {message.responded ? (
            <button
              onClick={() => markAsResponded(message.id, false)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Move back to new"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => markAsResponded(message.id, true)}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Mark as completed"
            >
              <CheckCircle className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {message.subject && (
        <div className="mb-3 text-sm sm:text-base">
          <span className="font-medium text-gray-700">Subject: </span>
          <span className="text-gray-900 break-words">{message.subject}</span>
        </div>
      )}

      {message.referral_source && (
        <div className="mb-3 text-sm sm:text-base">
          <span className="font-medium text-gray-700">Referral: </span>
          <span className="text-gray-900 break-words">{message.referral_source}</span>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
        <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap break-words">{message.message}</p>
      </div>

      {message.responded && message.responded_at && (
        <div className="mt-3 flex items-center gap-2 text-xs text-green-700">
          <CheckCircle className="w-3.5 h-3.5" />
          <span>
            Completed {message.responded_by ? `by ${message.responded_by}` : ''} on {new Date(message.responded_at).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );

  const renderMessages = () => {
    const newMessages = contactMessages.filter(m => !m.responded);
    const completedMessages = contactMessages.filter(m => m.responded);
    const displayedMessages = messagesTab === 'new' ? newMessages : completedMessages;

    return (
      <div className="space-y-6">
        <div>
          <div className="flex items-center space-x-3">
            <Mail className="w-6 sm:w-7 h-6 sm:h-7 text-gray-900" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Join Requests</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Requests from the website contact form</p>
        </div>

        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setMessagesTab('new')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              messagesTab === 'new'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            New
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              messagesTab === 'new'
                ? 'bg-blue-100 text-blue-700'
                : newMessages.length > 0
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {newMessages.length}
            </span>
          </button>
          <button
            onClick={() => setMessagesTab('completed')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              messagesTab === 'completed'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Completed
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              messagesTab === 'completed'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {completedMessages.length}
            </span>
          </button>
        </div>

        {displayedMessages.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
            {messagesTab === 'new' ? (
              <>
                <CheckCircle className="h-10 w-10 sm:h-12 sm:w-12 text-green-400 mx-auto mb-4" />
                <p className="text-sm sm:text-base text-gray-600">All caught up! No new requests.</p>
              </>
            ) : (
              <>
                <Mail className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-sm sm:text-base text-gray-600">No completed requests yet.</p>
              </>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="divide-y divide-gray-200">
              {displayedMessages.map((message) => renderMessageCard(message))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAccount = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center space-x-3">
          <User className="w-6 sm:w-7 h-6 sm:h-7 text-gray-900" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Account Settings</h1>
        </div>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your account settings</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Email</h3>
          <p className="text-sm sm:text-base text-gray-700 break-words">{userEmail}</p>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
          <ChangePasswordForm />
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Delete Account</h3>
          <DeleteAccountSection userEmail={userEmail} />
        </div>
      </div>
    </div>
  );

  const renderRequests = () => (
    <div className="space-y-6">
      <div>
        <div className="flex items-center space-x-3">
          <MessageCircle className="w-6 sm:w-7 h-6 sm:h-7 text-gray-900" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Prayer & Praise Requests</h1>
        </div>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Community prayer and praise submissions</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <MessageCircle className="w-7 sm:w-8 h-7 sm:h-8 text-blue-600 mr-3 flex-shrink-0" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-blue-600">Total Requests</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-900">{requests.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <Heart className="w-7 sm:w-8 h-7 sm:h-8 text-green-600 mr-3 flex-shrink-0" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-green-600">Prayer Requests</p>
              <p className="text-xl sm:text-2xl font-bold text-green-900">
                {requests.filter(r => r.type === 'prayer').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <Users className="w-7 sm:w-8 h-7 sm:h-8 text-teal-600 mr-3 flex-shrink-0" />
            <div>
              <p className="text-xs sm:text-sm font-medium text-teal-600">Praise Reports</p>
              <p className="text-xl sm:text-2xl font-bold text-teal-900">
                {requests.filter(r => r.type === 'praise').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
          <MessageCircle className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm sm:text-base text-gray-600">No requests submitted yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-200">
            {requests.map((request) => (
              <div key={request.id} className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-full flex-shrink-0 ${
                        request.type === 'prayer'
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-green-100 text-green-600'
                      }`}>
                        {request.type === 'prayer' ? (
                          <MessageCircle className="w-4 sm:w-5 h-4 sm:h-5" />
                        ) : (
                          <Heart className="w-4 sm:w-5 h-4 sm:h-5" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 capitalize">
                          {request.type} Request
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 break-words">
                          {request.name ? `From: ${request.name}` : 'Anonymous'} •
                          {' '}{new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap break-words">{request.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteRequest(request.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg sm:rounded-full transition-colors self-end sm:self-start"
                    title="Delete request"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    if (!canAccess(activeTab) && activeTab !== 'account') {
      return renderNoAccess();
    }
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'events':
        return renderEvents();
      case 'messages':
        return renderMessages();
      case 'requests':
        return renderRequests();
      case 'members':
        return <AdminMembers />;
      case 'reading':
        return <AdminReading />;
      case 'account':
        return renderAccount();
      default:
        return renderNoAccess();
    }
  };

  return (
    <>
      <AdminAddEventModal
        isOpen={showAddEventModal}
        onClose={() => setShowAddEventModal(false)}
        onSubmit={createEvent}
      />
      {editingEvent && (
        <AdminAddEventModal
          key={editingEvent.id}
          isOpen
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSubmit={(data) => updateEvent(editingEvent.id, data)}
        />
      )}
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        {/* Mobile Menu Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg text-gray-900"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          ref={sidebarRef}
          className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white shadow-lg flex flex-col transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Admin Panel</h2>
          </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      setActiveTab(item.id);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-3 py-2.5 rounded-lg transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="ml-3 font-medium flex-1 text-left">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-1 flex-shrink-0">
                        {item.badge}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-200" ref={accountMenuRef}>
          <div className="relative">
            <button
              onClick={() => setAccountMenuOpen(!accountMenuOpen)}
              className="w-full flex items-center px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                {userEmail ? userEmail.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="ml-3 flex-1 text-left">
                <p className="text-sm font-medium text-gray-900">{userEmail || 'Admin'}</p>
              </div>
              <ChevronUp className={`w-4 h-4 transition-transform ${accountMenuOpen ? '' : 'rotate-180'}`} />
            </button>

            {accountMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                <button
                  onClick={() => {
                    setActiveTab('account');
                    setAccountMenuOpen(false);
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="ml-3 font-medium">Account</span>
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="ml-3 font-medium">Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8 max-w-7xl">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
